import { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { VIDEOS, CATEGORIES } from '../videos/videos.data';
import SeoMeta from '../components/SeoMeta';
import { usePageMeta } from '../hooks/usePageMeta';

// ====== styled ======
const Page = styled.section`
  padding: 48px 0 72px;
  .container{ width:${({theme})=>theme.container}; margin:0 auto; }
  .section-head{ display:flex; align-items:end; justify-content:space-between; gap:16px; margin-bottom:18px; }
  .kicker{ font-size:14px; opacity:.7; text-transform:uppercase; letter-spacing:.12em; }
`;
const Toolbar = styled.div`
  display:flex; flex-wrap:wrap; gap:8px; margin: 0 0 16px;
  .chip{
    padding:8px 12px; border:1px solid ${({theme})=>theme.border};
    background:${({theme})=>theme.surface}; border-radius:${({theme})=>theme.radius.pill}px;
    cursor:pointer; user-select:none;
  }
  .chip.active{ outline:2px solid ${({theme})=>theme.accent}; }
`;
const Grid = styled.div`
  display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap:12px;
  @media (max-width: 1280px){ grid-template-columns: repeat(3, minmax(0,1fr)); }
  @media (max-width: 980px){ grid-template-columns: repeat(2, minmax(0,1fr)); }
  @media (max-width: 560px){ grid-template-columns: 1fr; }
`;
const Card = styled.article`
  border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px; overflow:hidden; box-shadow:${({theme})=>theme.shadow.sm};
  display:flex; flex-direction:column; min-height: 100%;
  .frame{ position:relative; aspect-ratio:16/9; background:#000; }
  iframe{ position:absolute; inset:0; width:100%; height:100%; border:0; }
  .cap{ padding:10px 12px; display:grid; gap:6px; }
  .title{ font-weight:700; font-size:14px; }
  .meta{ font-size:12px; opacity:.8; }
  .tags{ display:flex; flex-wrap:wrap; gap:6px; }
  .tag{ font-size:12px; padding:4px 8px; border:1px solid ${({theme})=>theme.border}; border-radius:${({theme})=>theme.radius.pill}px; }
`;
const Pager = styled.nav`
  display:flex; gap:6px; justify-content:center; margin-top:16px; flex-wrap:wrap;
  .p{
    min-width:36px; height:36px; display:grid; place-items:center;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.md}px; cursor:pointer; user-select:none;
  }
  .p.active{ outline:2px solid ${({theme})=>theme.accent}; font-weight:700; }
  .p[disabled]{ opacity:.5; pointer-events:none; }
`;

// ====== helpers ======
const PER_PAGE = 20;

function filterByCategory(list, categoryKey) {
  if (!categoryKey || categoryKey === 'all') return list;
  return list.filter(v => v.category === categoryKey);
}
function paginate(list, page, perPage = PER_PAGE) {
  const start = (page - 1) * perPage;
  return list.slice(start, start + perPage);
}

export default function VideosPage() {
  const meta = usePageMeta('videos');
  const [cat, setCat] = useState('all');
  const [page, setPage] = useState(1);

  // Защита от “прыжков”: фиксированный min-height для грида по количеству карточек
  // и отключаем AOS повторную инициализацию
  useEffect(()=>{ AOS.init({ once:true, duration:600, offset:80, easing:'ease-out-cubic' }); },[]);

  // Фильтрация/пагинация
  const filtered = useMemo(()=> filterByCategory(VIDEOS, cat), [cat]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const pageItems = useMemo(()=> paginate(filtered, pageSafe), [filtered, pageSafe]);

  useEffect(()=>{ setPage(1); }, [cat]); // сбрасываем страницу при смене категории

  return (
    <Page>
      <SeoMeta
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        image={meta.image}
        path={meta.path || '/info/videos'}
      />
      <div className="container">
        <div className="section-head">
          <div>
            <div className="kicker">Відео</div>
            <h1>Відео по товарам та послугам</h1>
          </div>
        </div>

        {/* Categories */}
        <Toolbar role="tablist" aria-label="Категорії відео">
          <div
            className={`chip ${cat==='all'?'active':''}`}
            role="tab"
            aria-selected={cat==='all'}
            onClick={()=>setCat('all')}
          >Всі</div>
          {CATEGORIES.map(c=>(
            <div
              key={c.k}
              className={`chip ${cat===c.k?'active':''}`}
              role="tab"
              aria-selected={cat===c.k}
              onClick={()=>setCat(c.k)}
            >
              {c.title}
            </div>
          ))}
        </Toolbar>

        {/* Grid */}
        <Grid>
          {pageItems.map((v, i)=>(
            <Card key={`${v.id}-${i}`} data-aos="fade-up" data-aos-delay={(i%8)*40}>
              <div className="frame">
                <iframe
                  loading="lazy"
                  src={`https://www.youtube.com/embed/${v.youtubeId}?rel=0`}
                  title={v.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <div className="cap">
                <div className="title">{v.title}</div>
                <div className="meta">{CATEGORIES.find(c=>c.k===v.category)?.title || '—'}</div>
                {v.tags?.length>0 && (
                  <div className="tags">
                    {v.tags.map((t,idx)=><span className="tag" key={idx}>#{t}</span>)}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </Grid>

        {/* Pager */}
        <Pager aria-label="Навігація сторінками">
          <button className="p" disabled={pageSafe===1} onClick={()=>setPage(p=>Math.max(1,p-1))}>‹</button>
          {Array.from({length: totalPages}).map((_,i)=>{
            const p = i+1;
            return (
              <button key={p} className={`p ${p===pageSafe?'active':''}`} onClick={()=>setPage(p)}>{p}</button>
            );
          })}
          <button className="p" disabled={pageSafe===totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>›</button>
        </Pager>

        {/* Подсказка */}
        <p style={{marginTop:10, fontSize:12, opacity:.7}}>
          Всього відео: {filtered.length} • Сторінок: {totalPages} • По {PER_PAGE} на сторінку
        </p>
      </div>
    </Page>
  );
}
